import { TouchControls } from './TouchControls';

export interface InputState {
  dx: number;     // -1 / 0 / 1 left/right strafe (WASD / left joystick)
  dz: number;     // -1 / 0 / 1 forward/backward (WASD / left joystick)
  turn: number;   // -1 = turn body+cam left, +1 = right (ArrowLeft/J/Q/E/L / right joystick X)
  firing: boolean;
  seq: number;
  orbitV: number; // -1 = tilt camera lower, +1 = tilt higher (ArrowUp/I/Down/K / right joystick Y)
  mouseX: number; // raw mouse movementX when pointer-locked (pixels)
  mouseY: number; // raw mouse movementY when pointer-locked (pixels)
}

export class InputSystem {
  private keys:  Set<string> = new Set();
  private seq:   number = 0;
  private touch: TouchControls;
  private canvas: HTMLCanvasElement;

  /** Accumulated mouse movement since last capture() (only while pointer-locked) */
  private mouseDX = 0;
  private mouseDY = 0;
  private _pointerLocked = false;

  constructor(_app: unknown, canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.touch = new TouchControls();
    canvas.style.touchAction = 'none';
    this.setupListeners();
  }

  /** Whether the pointer is currently locked */
  get pointerLocked(): boolean { return this._pointerLocked; }

  private setupListeners() {
    window.addEventListener('keydown', (e) => this.keys.add(e.code));
    window.addEventListener('keyup',   (e) => this.keys.delete(e.code));

    // Mouse buttons — left click fires
    window.addEventListener('mousedown', (e) => { if (e.button === 0) this.keys.add('Mouse0'); });
    window.addEventListener('mouseup',   (e) => { if (e.button === 0) this.keys.delete('Mouse0'); });

    // ── Pointer lock ─────────────────────────────────────────
    // Click canvas to request pointer lock
    this.canvas.addEventListener('click', () => {
      if (!document.pointerLockElement) {
        this.canvas.requestPointerLock();
      }
    });

    document.addEventListener('pointerlockchange', () => {
      this._pointerLocked = document.pointerLockElement === this.canvas;
      if (!this._pointerLocked) {
        this.mouseDX = 0;
        this.mouseDY = 0;
      }
    });

    // Accumulate mouse deltas while locked
    window.addEventListener('mousemove', (e) => {
      if (this._pointerLocked) {
        this.mouseDX += e.movementX;
        this.mouseDY += e.movementY;
      }
    });

    // Escape releases pointer lock (browsers do this natively, but we also
    // want Escape to open menus — so we let the browser handle the unlock).
  }

  capture(): InputState {
    const t = this.touch.getState();

    let dx = 0;
    let dz = 0;
    if (this.keys.has('KeyW')) dz -= 1;
    if (this.keys.has('KeyS')) dz += 1;
    if (this.keys.has('KeyA')) dx -= 1;
    if (this.keys.has('KeyD')) dx += 1;

    if (dx === 0) dx = t.dx;
    if (dz === 0) dz = t.dz;

    if (dx !== 0 && dz !== 0) {
      const len = Math.sqrt(dx * dx + dz * dz);
      dx /= len;
      dz /= len;
    }

    const firing = this.keys.has('Space') || this.keys.has('Mouse0') || t.firing;

    let turn = 0;
    if (this.keys.has('ArrowLeft')  || this.keys.has('KeyQ') || this.keys.has('KeyJ')) turn -= 1;
    if (this.keys.has('ArrowRight') || this.keys.has('KeyE') || this.keys.has('KeyL')) turn += 1;
    if (turn === 0) turn = t.turn;

    let orbitV = 0;
    if (this.keys.has('ArrowUp')   || this.keys.has('KeyI')) orbitV += 1;
    if (this.keys.has('ArrowDown') || this.keys.has('KeyK')) orbitV -= 1;
    if (orbitV === 0) orbitV = t.orbitV;

    // Consume accumulated mouse delta (reset for next frame)
    const mx = this.mouseDX;
    const my = this.mouseDY;
    this.mouseDX = 0;
    this.mouseDY = 0;

    return { dx, dz, turn, firing, seq: this.seq++, orbitV, mouseX: mx, mouseY: my };
  }

  /** Release pointer lock (e.g. when opening menus) */
  exitPointerLock() {
    if (document.pointerLockElement) document.exitPointerLock();
  }

  isMenuOpen(): boolean {
    return this.keys.has('KeyU');
  }

  destroy() {
    this.touch.destroy();
    if (document.pointerLockElement === this.canvas) document.exitPointerLock();
  }
}
